// Страница документации «Фид каталога из кабинета». main CatalogFeedController передаёт тело в catalog
// (ProductController::handleCatalogFeed): проверка — CatalogFeedRequest, импорт — CatalogFeedImporter,
// картинки — CatalogFeedImages. Метод кабинета: нужен токен входа руководителя.
import type { ApiDocPage } from '../types'

export const feedApiDocs: Record<string, ApiDocPage> = {
  feedCatalog: {
    link: ['POST /api/dashboard/v1/chief/market/feeds/catalog'],
    blocks: [
      {
        id: 'about',
        heading: 'Описание',
        text: [
          'Метод кабинета: его вызывает руководитель компании с токеном входа в кабинет (Authorization: Bearer). Тело — JSON.',
          'Для обмена с учётной системой или CRM есть тот же импорт в API интеграций: POST /api/integration/v1/import/catalog (раздел «Каталог»). Формат тела тот же; там же есть удаление, выгрузка для сверки и полная синхронизация.',
          'Все разделы тела не обязательны: можно прислать только товары, только категории и так далее. Связи между записями — по ext_id, вашему идентификатору записи.',
          'Записи с уже известным ext_id обновляются, с новым — создаются. Обновляются только присланные поля. Повтор того же запроса безопасен — дублей не будет.',
          'Записи, которых нет в запросе, не удаляются. Товар или предложение, удалённые раньше, с тем же ext_id восстанавливаются.',
        ],
      },
      {
        id: 'params',
        heading: 'Пример тела запроса',
        nav: 'Пример запроса',
        code: `{
  "categories": [
    { "ext_id": "cat-1", "name": "Одежда", "parent_ext_id": null, "active": true, "image_url": "https://shop.example.com/img/cat-1.jpg" },
    { "ext_id": "cat-2", "name": "Футболки", "parent_ext_id": "cat-1" }
  ],
  "tags": [
    { "ext_id": "tag-1", "value": "Новинка" }
  ],
  "components": [
    { "ext_id": "comp-1", "name": "Хлопок" }
  ],
  "properties": [
    { "ext_id": "prop-1", "name": "Цвет", "title": "color", "type": "enum" },
    { "ext_id": "prop-2", "name": "Страна", "title": "country", "type": "string" }
  ],
  "property_enum_values": [
    { "ext_id": "prop-1-red", "property_ext_id": "prop-1", "value": "Красный" },
    { "ext_id": "prop-1-blue", "property_ext_id": "prop-1", "value": "Синий" }
  ],
  "products": [
    {
      "ext_id": "prod-1",
      "title": "Футболка",
      "description": "Футболка из хлопка",
      "simple": false,
      "article": "TS-01",
      "sort": 500,
      "active": true,
      "categories": ["cat-2"],
      "tags": ["tag-1"],
      "similar": ["prod-2"],
      "string_property_values": [
        { "property_ext_id": "prop-2", "value": "Россия" }
      ],
      "image_urls": ["https://shop.example.com/img/prod-1-2.jpg"],
      "main_image_url": "https://shop.example.com/img/prod-1.jpg"
    },
    {
      "ext_id": "prod-2",
      "title": "Носки",
      "description": "Носки из хлопка",
      "price": 300,
      "old_price": 400,
      "barcode": "4600000000017",
      "bonus": 10,
      "categories": ["cat-1"],
      "components": [
        { "ext_id": "comp-1", "quantity": 80, "unit": "percent" }
      ],
      "enum_property_values": ["prop-1-blue"]
    }
  ],
  "offers": [
    {
      "ext_id": "prod-1-red",
      "product_ext_id": "prod-1",
      "title": "Футболка, красная",
      "description": "Красная футболка из хлопка",
      "price": 1200,
      "old_price": 1500,
      "barcode": "4600000000024",
      "sort": 10,
      "enum_property_values": ["prop-1-red"],
      "main_image_url": "https://shop.example.com/img/prod-1-red.jpg"
    },
    {
      "ext_id": "prod-1-blue",
      "product_ext_id": "prod-1",
      "title": "Футболка, синяя",
      "description": "Синяя футболка из хлопка",
      "price": 1200,
      "by_order": true,
      "enum_property_values": ["prop-1-blue"]
    }
  ]
}`,
      },
      {
        id: 'categories',
        heading: 'Категории, теги, компоненты, характеристики',
        nav: 'Справочники',
        text: [
          'categories: ext_id — обязательно, не повторяется в запросе; name — обязательно, до 255 символов; parent_ext_id — ext_id родительской категории из этого запроса или уже загруженной, null — категория верхнего уровня, поле не передано — родитель не меняется; active — true/false, не обязательно (новая категория по умолчанию активна); image_url — не обязательно, адрес картинки; null или пустая строка убирают картинку.',
          'Если родительская категория не найдена, категория сохраняется без смены родителя, а ошибка попадает в errors ответа.',
          'tags: ext_id и value (название, до 255 символов) — обязательны.',
          'components: ext_id — обязательно, не повторяется; name — обязательно, до 255 символов.',
          'properties: ext_id — обязательно, не повторяется; name — название, обязательно, до 255 символов; title — обозначение, обязательно; type — обязательно, enum (список значений) или string (строка); active — true/false, не обязательно (по умолчанию true).',
          'property_enum_values — значения характеристик-списков: ext_id — обязательно, не повторяется; property_ext_id — ext_id характеристики, обязательно; value — обязательно, до 255 символов. Если характеристика не найдена, значение не сохраняется.',
        ],
      },
      {
        id: 'products',
        heading: 'Товары',
        text: [
          'Обязательны: ext_id (не повторяется в запросе), title (до 255 символов), description, categories.',
          'categories — массив ext_id категорий товара, хотя бы одна. Каждая категория должна быть в этом же запросе или уже загружена, иначе весь запрос отклоняется с ошибкой 422.',
          'Не обязательны: article и barcode (до 255 символов), sort (число), bonus_multiplier, active, new, preorderable, popular, special, extra, by_order (true/false; by_order: true — «нет в наличии»).',
          'tags — ext_id тегов; similar — ext_id похожих товаров (товаров, не предложений; можно ссылаться на товары ниже в том же запросе).',
          'components — состав: ext_id компонента, quantity (число не меньше 0) и unit — код единицы измерения. Коды — активные единицы справочника единиц измерения; по умолчанию это piece (шт), kg (кг), gr (гр), l (л), ml (мл), percent (%), permille (‰).',
          'enum_property_values — ext_id значений характеристик-списков; string_property_values — значения характеристик-строк: property_ext_id и value (строка), оба обязательны.',
          'Переданный список tags, similar, components или enum_property_values заменяет прежний целиком. Не переданный — не меняется.',
          'Тег, похожий товар, компонент, значение или характеристика, которых нет, не мешают сохранить товар: товар сохраняется без них, а ошибка попадает в errors ответа.',
        ],
      },
      {
        id: 'simple',
        heading: 'Простой товар и товар с вариантами',
        nav: 'Простой товар',
        text: [
          'Простой товар продаётся одним предложением с тем же ext_id, что у товара (как в обмене с 1С у товара без характеристик). Цену, старую цену, скидку, бонусы и штрихкод указывайте у самого товара — они сохраняются в этом предложении.',
          'Товар с вариантами продаётся предложениями из раздела offers. price, old_price, discount и bonus у такого товара не применяются, даже если переданы.',
          'simple передавать не нужно: товар считается простым, если у него нет своих предложений — ни в этом запросе, ни в каталоге (в том числе заведённых в кабинете). simple: true или simple: false задают это явно.',
          'Когда для товара приходит предложение в offers, единственное предложение убирается и при следующих выгрузках товара не возвращается.',
          'Прежний ext_id единственного предложения «{ext_id товара}_single_offer» по-прежнему принимается в offers и при удалении — это то же предложение.',
        ],
      },
      {
        id: 'offers',
        heading: 'Торговые предложения',
        nav: 'Предложения',
        text: [
          'Обязательны: ext_id (не повторяется в запросе и не совпадает с ext_id товара), product_ext_id — ext_id товара из этого запроса или загруженного раньше, title, description, price.',
          'Если товар не найден, предложение не сохраняется, а ошибка попадает в errors ответа.',
          'Не обязательны: article, barcode, old_price, discount, bonus, sort (число не меньше 0), bonus_multiplier, active, popular, by_order (true/false).',
          'by_order, если не передан, берётся у товара. При импорте товара его by_order переносится на все его предложения.',
          'components, enum_property_values, string_property_values, image_urls и main_image_url — как у товара.',
        ],
      },
      {
        id: 'prices',
        heading: 'Цены и скидки',
        nav: 'Цены',
        text: [
          'price, old_price и bonus — целые числа не меньше 0 (цены хранятся целыми рублями, дробная price или old_price — ошибка 422); discount — процент от 0 до 100.',
          'old_price передаётся вместе с price и не может быть меньше её — иначе весь запрос отклоняется с ошибкой 422.',
          'Старая цена и скидка согласуются сами. Передана old_price — скидка считается от неё (если переданы и old_price, и discount, главнее old_price). Передана только discount — старая цена вычисляется из цены и скидки. Передана только price — скидка пересчитывается от прежней старой цены.',
        ],
      },
      {
        id: 'images',
        heading: 'Картинки',
        text: [
          'main_image_url — главное фото, image_urls — остальные фото в нужном порядке; у категории — image_url. Адреса — http или https, доступные из интернета.',
          'Картинки скачиваются в фоне после ответа: в ответе только их число (images_queued). Принимаются JPEG, PNG, GIF и WebP.',
          'Картинка с тем же адресом повторно не скачивается. Адрес, пропавший из image_urls, удаляется; пустой image_urls удаляет фото из фида. null в main_image_url убирает главное фото.',
          'Адреса, которые не удалось скачать, видны в выгрузке каталога API интеграций (поле image_errors).',
        ],
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        text: [
          'Ответ без обёртки success/data/error — сразу итог импорта.',
          'created, updated, restored — сколько записей создано, обновлено и восстановлено; failed — сколько не сохранено. В sections — те же числа по разделам запроса.',
          'Каждая позиция сохраняется отдельно: ошибка одной не отменяет остальные. errors — ошибки по позициям: раздел, ext_id, путь до позиции в запросе и текст. Ошибка может относиться и к сохранённой позиции, если не применилась её часть (например, не найден тег).',
          'images_queued — сколько записей ждут загрузки картинок.',
        ],
        code: `{
  "message": "ok",
  "created": 3,
  "updated": 10,
  "restored": 1,
  "failed": 1,
  "errors": [
    {
      "section": "offers",
      "ext_id": "prod-9-red",
      "path": "offers.4",
      "message": "Товар с ext_id 'prod-9' не найден — пришлите его в разделе products"
    }
  ],
  "sections": {
    "products": { "created": 1, "updated": 6, "restored": 1, "failed": 0 },
    "offers": { "created": 2, "updated": 4, "restored": 0, "failed": 1 }
  },
  "images_queued": 7
}`,
      },
      {
        id: 'errors',
        heading: 'Ошибки',
        text: [
          '422 — ошибка формата (нет обязательного поля, неверный тип, неизвестная категория товара): ничего не сохранено. В message — первая ошибка, в errors — все ошибки по полям, например products.3.title.',
          '409 — идёт другой импорт каталога этой компании: запрос ждёт до 90 секунд, затем отвечает 409. Повторите запрос позже.',
          '401 — нет токена руководителя или он устарел.',
        ],
        code: `{
  "message": "<текст первой ошибки>",
  "errors": {
    "products.3.title": ["<текст ошибки>"]
  }
}`,
      },
      {
        id: 'timeout',
        heading: 'Если ответ не пришёл',
        text: [
          'Ответ 504 «Каталог не ответил за 110 секунд» значит, что импорт мог примениться. Повторите запрос — повтор безопасен — или сверьте каталог. Запрос не повторяется автоматически.',
          'Чтобы не упираться во время, присылайте большой каталог пачками.',
        ],
      },
    ],
  },
}
